import React from "react";
import PropTypes from "prop-types";
import Button from "react-toolbox/lib/button/Button";
import "./ValidatedForm.css";

export const ValidationContext = React.createContext({
	valueChanged: () => null,
	fieldValues: {},
	disabledStates: {}
});

/*
	This makes it easy to display and gather form data.
	Pass initial values in through the `initialValues` prop.
	Pass a FormValidator in to do validations.
	The function you pass into the `onSubmit` prop will get 
	called with a new object containing all the values from
	the fields.

	This doesn't work with [controlled components](https://reactjs.org/docs/forms.html), 
	it only works with with [uncontrolled components](https://reactjs.org/docs/uncontrolled-components.html).
	It makes data available to the child components via the ValidationContext. It also makes a 
	`valueChanged` function available that child objects should use as their `onChanged` function
	to set field value.

	The adapters in FormAdapters.js work well as children of ValidatedForm.

	You can pass in a `FieldValueTranslator` in the `formDataTranslators` property to translate
	field values. The field names in the `formDataTranslators` object should match the field names
	in the data. It is sometimes more convenient, however, to put the translator on the adapter object
	instead. In that case, you souldn't pass in a translator to this form object, unless you need 
	multiple levels of translation.
*/
class ValidatedForm extends React.Component {
	constructor(props) {
		super(props);

		this.handleChange = this.handleChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);

		//This will call itself recursively to support fields being embedded
		//in other components, like `<div>`. 
		function getAllFieldNames(children) {
			var allFormFields = {};
			if (Array.isArray(children)) {
				children.reduce((aggregator, child) => {
					let fieldName =  child ? (child.props ? child.props.fieldName : null) : null;
					if (fieldName) {
						aggregator[fieldName] = null;
					} else if (child && child.props && child.props.children) {
						const subFields = getAllFieldNames(child.props.children);
						Object.assign(aggregator, subFields);
					}
					return aggregator;
				}, allFormFields);
			} else {
				let fieldName = children.fieldName;
				if (fieldName) {
					allFormFields[fieldName] = null;
				} else if (children.props && children.props.children) {
					const subFields = getAllFieldNames(children.props.children);
					Object.assign(allFormFields, subFields);
				}
			}

			return allFormFields;
		}

		const allFormFields = getAllFieldNames(this.props.children);

		let rawValues = Object.assign({}, allFormFields, props.initialValues); 

		//translate all default values to display value
		let fieldValues = Object.keys(rawValues).reduce((aggregator, fieldName) => {
			//give translators a chance to translate null values.
			var val = rawValues[fieldName];
			let translator = props.formDataTranslators ? props.formDataTranslators[fieldName] : null;
			if (translator) {
				val = translator.toDisplayValue(val);
			}
			aggregator[fieldName] = val;
			return aggregator;
		}, {});

		const disabledStates = this.determineDisabledStates(null, fieldValues);

		let dataValidationMessages = props.formValidator ? props.formValidator.validateForm(fieldValues) : null;
		let formValid = dataValidationMessages == null;

		this.state = {
			fieldValues: fieldValues,
			disabledStates: disabledStates,
			dataValidationMessage: dataValidationMessages,
			validationMessages: {},
			formValid: formValid, 
			submitting: false,
			hasEverSubmitted: false
		};
	}

	handleChange(fieldName, value) {
		//fieldValues, and returns an object with newly calculated values.
		// console.log("Change to field "+fieldName+". New value = ", value);
		//let newFieldValues = Object.assign({}, this.state.fieldValues, {[fieldName]: value});
		let curFieldValues = Object.assign({}, this.state.fieldValues);
		let newFieldValues = this.props.fieldValueCalculator ? 
			this.props.fieldValueCalculator(fieldName, value, curFieldValues) : null;
		if (newFieldValues === null) {
			newFieldValues = Object.assign(curFieldValues, { [fieldName]: value });
		}

		let fieldMessage = this.props.formValidator ? 
			{
				[fieldName]: this.props.formValidator.validateField(fieldName, value, this.state.fieldValues)
			} :
			{};
		let validationMessages = Object.assign({}, this.state.validationMessages, fieldMessage);
		let allValidationMessages = Object.values(validationMessages);

		let dataValidationMessage = this.props.formValidator ? 
			this.props.formValidator.validateForm(newFieldValues) : null;
		let formValid =  
			dataValidationMessage == null &&
			(allValidationMessages.length === 0 || (allValidationMessages.every(val => val == null)));

		const disabledStates = this.determineDisabledStates(this.state.disabledStates, newFieldValues);

		this.setState({
			fieldValues: newFieldValues,
			disabledStates: disabledStates,
			dataValidationMessage: dataValidationMessage,
			validationMessages: validationMessages,
			formValid: formValid
		});
	}

	determineDisabledStates(currentDisabledStates, fieldValues) {
		const fieldDisablers = this.props.formFieldDisablers;
			
		var newStates = currentDisabledStates ? Object.assign({}, currentDisabledStates) : {};
		if (fieldDisablers) {
			const fieldNames = Object.keys(fieldDisablers);
			//all fields need a chance to update their disabled state when one field changes
			fieldNames.forEach((fieldName) => {
				const disabler = fieldDisablers[fieldName];
				newStates[fieldName] = disabler ? 
					disabler(fieldName, fieldValues[fieldName], fieldValues) : false;
			});
		}
		
		return newStates;
	}

	handleSubmit(event) {
		event.preventDefault();
		//Prepare the form first, then send the data
		//This also allows us to handle the server field validation errors better.
		//See `componentDidUpdate` below.
		this.setState({submitting: true, hasEverSubmitted: true});
	}

	render() {
		let externalValidationMsg = this.props.dataValidationMessage ?
			<p className="formValidationErrorMsg">
				{this.props.dataValidationMessage}
			</p> : null;
		let internalValidationMsg = this.state.dataValidationMessage ?
			<p className="formValidationErrorMsg">
				{this.state.dataValidationMessage}
			</p> : null;
		let submitBtnLabel = this.props.submitButtonLabel ? this.props.submitButtonLabel : "Submit";
		let submitBtn = <Button
			raised primary   
			label={submitBtnLabel}
			type="submit"
			className="validatedFormSubmitButton" 
			disabled={!this.state.formValid || this.state.submitting}
		/>;
		//Local messages should supercede server messages.
		let validationMessages = Object.assign({}, this.props.validationMessages, this.state.validationMessages);
		let validationContextData = {
			valueChanged: this.handleChange,
			fieldValues: this.state.fieldValues,
			disabledStates: this.state.disabledStates,
			validationMessages: validationMessages
		};
		return (
			<form onSubmit={this.handleSubmit}>
				{externalValidationMsg}
				<div className="ValidatedFormFields">
					<ValidationContext.Provider value={validationContextData}>
						{this.props.children}
					</ValidationContext.Provider>
				</div>
				<br/>
				{submitBtn}
				{internalValidationMsg}
			</form>
		);
	}

	componentDidUpdate(prevProps, prevState) { // eslint-disable-line no-unused-vars
		if (this.state.submitting && !prevState.submitting) {
			//Submit the form data, after translating display values to data values
			// console.log("Submitting. State values = ", this.state.fieldValues);
			let formData = Object.keys(this.state.fieldValues).reduce((aggregator, fieldName) => {
				//give translators a chance to translate null values.
				var val = this.state.fieldValues[fieldName];
				let translator = this.props.formDataTranslators ? this.props.formDataTranslators[fieldName] : null;
				if (translator) {
					val = translator.toStorageValue(val);
				}
				aggregator[fieldName] = val;
				return aggregator;
			}, {});
			// console.log("formData = ", formData);
			this.props.onSubmit(formData);
		} else if (this.state.submitting && prevState.submitting) {
			//The form was rendered again by the parent, so assume we are being reshown after
			//the submit.
			//Make this object's validation messages match those from the server
			let validationMsgs = Object.assign({}, this.props.validationMessages);
			this.setState({
				validationMessages: validationMsgs,
				submitting: false
			});
		}
	}
}

ValidatedForm.propTypes = {
	children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	initialValues: PropTypes.object,
	dataValidationMessage: PropTypes.string,
	validationMessages: PropTypes.object,
	formValidator: PropTypes.object,
	formDataTranslators: PropTypes.object,
	fieldValueCalculator: PropTypes.func,
	formFieldDisablers: PropTypes.object,
	submitButtonLabel: PropTypes.string,
	onSubmit: PropTypes.func
};

export class FormValidator {
	constructor(fieldValidators, formValidator) {
		this.fieldValidators = fieldValidators ? fieldValidators : {};
		this.formValidator = formValidator;
	}

	//Returns a string describing error, or null
	validateField(fieldName, value, formData) {
		let validator = this.fieldValidators ? this.fieldValidators[fieldName] : null;
		return validator ? validator(value, formData) : null;
	}

	//Returns a string describing error, or null
	validateForm(formData) {
		return this.formValidator ? this.formValidator(formData) : null;
	}

}

export class FormFieldDisabler {
	constructor(fieldDisablers) {
		this.fieldDisablers = fieldDisablers;
	}

	shouldFieldBeDisabled(fieldName, value, formData){
		let disabler = this.fieldDisablers ? this.fieldDisablers[fieldName] : null;
		return disabler ? disabler(value, formData) : false;
	}
}

export const UtilFieldValidators = {
	valueRequired: (value) => {
		var msg = null;
		if (value == null) {
			msg = "This field is required";
		} else 
		if (typeof value === String && value === "") {
			msg = "This field is required";
		}
		return msg;
	}
};

export const UtilDataValidators = {
	allFieldsRequired: (data) => {
		var msg = null;
		if (data == null ) {
			msg = "All Fields are required";
		} else {
			let values = Object.values(data);
			let isValPresent = val => val !== null ? (typeof val === "string" ? val !== "" : true) : false;
			msg = values.every(isValPresent) ? null : "* All Fields are required";
		}
		return msg;
	}
};

export default ValidatedForm;


// WEBPACK FOOTER //
// ./src/components/form/ValidatedForm.js